import { z } from 'zod';

// Настройки салона, которые владелец правит сам из кабинета.
// Демо: localStorage. Прод: tenants.business, services, staff и resources в D1.
// Формат совпадает с business.template.json,
// чтобы правки из кабинета и файл настроек не разъезжались.

const STORAGE_KEY = 'mnc-salon-settings';

const WEEK_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type WeekDay = (typeof WEEK_DAYS)[number];
export const WEEK_DAYS_ORDER: WeekDay[] = [...WEEK_DAYS];

export const DAY_LABELS: Record<WeekDay, string> = {
  mon: 'Пн',
  tue: 'Вт',
  wed: 'Ср',
  thu: 'Чт',
  fri: 'Пт',
  sat: 'Сб',
  sun: 'Вс'
};

const DayHoursSchema = z
  .object({ open: z.string(), close: z.string() })
  .nullable();

const AvailableTimesSchema = z.record(
  z.enum(WEEK_DAYS),
  z.array(z.string().regex(/^\d{2}:\d{2}$/)).max(48)
);

export const GalleryItemSchema = z.object({
  src: z.string(),
  caption: z.string()
});
export type GalleryItem = z.infer<typeof GalleryItemSchema>;

export const EditableServiceSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  price: z.number().int().nonnegative(),
  durationMinutes: z.number().int().positive(),
  description: z.string().optional(),
  popular: z.boolean().optional()
});
export type EditableService = z.infer<typeof EditableServiceSchema>;

export const EditableStaffSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  role: z.string().optional()
});
export type EditableStaff = z.infer<typeof EditableStaffSchema>;

export const SalonSettingsSchema = z.object({
  name: z.string().min(1),
  tagline: z.string().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  addressHint: z.string().optional(),
  mapUrl: z.string().optional(),
  about: z.string().optional(),
  schedule: z.record(z.enum(WEEK_DAYS), DayHoursSchema),
  availableTimes: AvailableTimesSchema.optional(),
  services: z.array(EditableServiceSchema),
  staff: z.array(EditableStaffSchema),
  gallery: z.array(GalleryItemSchema)
});
export type SalonSettings = z.infer<typeof SalonSettingsSchema>;

let provisionedDefaults: SalonSettings | null = null;

export const DEFAULT_SCHEDULE: Record<WeekDay, { open: string; close: string } | null> = {
  mon: { open: '10:00', close: '20:00' },
  tue: { open: '10:00', close: '20:00' },
  wed: { open: '10:00', close: '20:00' },
  thu: { open: '10:00', close: '20:00' },
  fri: { open: '10:00', close: '20:00' },
  sat: { open: '10:00', close: '18:00' },
  sun: null
};

export const DEFAULT_AVAILABLE_TIMES: Record<WeekDay, string[]> = {
  mon: ['10:00', '12:00', '14:00', '16:00', '18:00'],
  tue: ['10:00', '12:00', '14:00', '16:00', '18:00'],
  wed: ['10:00', '12:00', '14:00', '16:00', '18:00'],
  thu: ['10:00', '12:00', '14:00', '16:00', '18:00'],
  fri: ['10:00', '12:00', '14:00', '16:00', '18:00'],
  sat: ['10:00', '12:00', '14:00', '16:00'],
  sun: []
};

// Значения по умолчанию совпадают с демо-салоном, чтобы владелец сразу видел
// знакомые данные, а не пустую форму.
export function defaultSalonSettings(): SalonSettings {
  return provisionedDefaults ?? {
    name: 'Салон красоты',
    tagline: 'Маникюр и педикюр без ожидания',
    phone: '+7 999 123-45-67',
    address: 'ул. Примерная, 12',
    addressHint: 'Вход со двора, второй этаж',
    mapUrl: 'https://yandex.ru/maps/',
    about:
      'Небольшая студия в центре. Два мастера, три услуги, запись онлайн без звонков.',
    schedule: { ...DEFAULT_SCHEDULE },
    availableTimes: structuredClone(DEFAULT_AVAILABLE_TIMES),
    services: [
      { id: 'mani-classic', name: 'Маникюр классический', price: 2500, durationMinutes: 60, description: 'Обработка без покрытия', popular: false },
      { id: 'mani-gel', name: 'Маникюр + покрытие', price: 3500, durationMinutes: 90, description: 'Гель-лак, дизайн по желанию', popular: true },
      { id: 'pedi', name: 'Педикюр', price: 4000, durationMinutes: 90, description: 'Полная обработка стоп', popular: false }
    ],
    staff: [
      { id: 'anna', name: 'Анна', role: 'мастер маникюра' },
      { id: 'maria', name: 'Мария', role: 'мастер маникюра и педикюра' }
    ],
    gallery: [
      { src: '/gallery/01.jpg', caption: 'Гель-лак, нюд' },
      { src: '/gallery/02.jpg', caption: 'Тёплое покрытие' },
      { src: '/gallery/03.jpg', caption: 'Дизайн' }
    ]
  };
}

// Demo-сборка подхватывает business.json конкретного tenant до рендера каталога.
// Это оставляет localStorage-редактор рабочим, но убирает жёсткую привязку
// каждого нового Pages-проекта к данным «Салон Демо».
export function setProvisionedSalonSettings(input: unknown): void {
  const parsed = SalonSettingsSchema.safeParse(input);
  if (parsed.success) provisionedDefaults = parsed.data;
}

// Чтение правок владельца. Битый JSON не роняет кабинет — отдаём дефолт.
export function readSalonSettings(): SalonSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSalonSettings();
    const parsed = SalonSettingsSchema.safeParse(JSON.parse(raw));
    if (parsed.success) return parsed.data;
    // Частичные/старые данные: добираем недостающее дефолтом.
    const base = defaultSalonSettings();
    const loose = JSON.parse(raw) as Partial<SalonSettings>;
    return SalonSettingsSchema.parse({ ...base, ...loose });
  } catch {
    return defaultSalonSettings();
  }
}

export function writeSalonSettings(next: SalonSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* приватный режим — правки живут только на текущей странице */
  }
}

// Новый id для услуги/мастера без внешних зависимостей.
export function newSalonId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}
